/**
 * Procedural texture generation for the world module.
 * Everything here is CPU-side, deterministic and allocation-free at runtime
 * (it all runs once during init). No external assets, ever.
 */
import * as THREE from 'three';

/* ------------------------------------------------------------------ hashing */
export function hash2i(x, y, s) {
  let h = Math.imul(x | 0, 374761393) ^ Math.imul(y | 0, 668265263) ^ Math.imul(s | 0, 2246822519);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** Fast non-tiling 2D value noise in [0,1]. */
export function vnoise(x, y, s) {
  const xi = Math.floor(x), yi = Math.floor(y);
  const fx = x - xi, fy = y - yi;
  const ux = fx * fx * (3 - 2 * fx), uy = fy * fy * (3 - 2 * fy);
  const a = hash2i(xi, yi, s), b = hash2i(xi + 1, yi, s);
  const c = hash2i(xi, yi + 1, s), d = hash2i(xi + 1, yi + 1, s);
  const ab = a + (b - a) * ux, cd = c + (d - c) * ux;
  return ab + (cd - ab) * uy;
}
export function vfbm(x, y, s, oct = 3, gain = 0.5, lac = 2.0) {
  let a = 1, f = 1, sum = 0, norm = 0;
  for (let i = 0; i < oct; i++) { sum += a * vnoise(x * f, y * f, s + i * 131); norm += a; a *= gain; f *= lac; }
  return sum / norm;
}

/* ---------------------------------------------------------- tileable fields */
/** Seamless value-noise fbm over an NxN torus. Returns Float32Array in [0,1]. */
export function tileFbm(N, baseCells, seed, oct = 4, gain = 0.5) {
  const out = new Float32Array(N * N);
  let amp = 1, norm = 0;
  for (let o = 0; o < oct; o++) { norm += amp; amp *= gain; }
  amp = 1;
  let cells = baseCells;
  for (let o = 0; o < oct; o++) {
    const sc = cells / N, sd = seed + o * 7919;
    for (let j = 0; j < N; j++) {
      const fy = j * sc, jy = Math.floor(fy), ty = fy - jy;
      const sy = ty * ty * (3 - 2 * ty);
      const j0 = ((jy % cells) + cells) % cells, j1 = (j0 + 1) % cells;
      const row = j * N;
      for (let i = 0; i < N; i++) {
        const fx = i * sc, ix = Math.floor(fx), tx = fx - ix;
        const sx = tx * tx * (3 - 2 * tx);
        const i0 = ((ix % cells) + cells) % cells, i1 = (i0 + 1) % cells;
        const a = hash2i(i0, j0, sd), b = hash2i(i1, j0, sd);
        const c = hash2i(i0, j1, sd), d = hash2i(i1, j1, sd);
        const ab = a + (b - a) * sx, cd = c + (d - c) * sx;
        out[row + i] += amp * (ab + (cd - ab) * sy);
      }
    }
    amp *= gain; cells *= 2;
  }
  for (let k = 0; k < out.length; k++) out[k] /= norm;
  return out;
}

/** Seamless worley (cell) distance field over an NxN torus, in [0,1]. */
export function tileWorley(N, cells, seed, jitter = 1.0) {
  const out = new Float32Array(N * N);
  for (let j = 0; j < N; j++) {
    const y = (j / N) * cells;
    const yi = Math.floor(y);
    for (let i = 0; i < N; i++) {
      const x = (i / N) * cells, xi = Math.floor(x);
      let best = 9;
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        const cx = xi + dx, cy = yi + dy;
        const wx = ((cx % cells) + cells) % cells, wy = ((cy % cells) + cells) % cells;
        const px = cx + 0.5 + (hash2i(wx, wy, seed) - 0.5) * jitter;
        const py = cy + 0.5 + (hash2i(wx, wy, seed + 4211) - 0.5) * jitter;
        const d = (px - x) * (px - x) + (py - y) * (py - y);
        if (d < best) best = d;
      }
      out[j * N + i] = Math.min(1, Math.sqrt(best) / 0.85);
    }
  }
  return out;
}

/* ------------------------------------------------------------ texture build */
/**
 * Pack a height field into an RGBA detail texture:
 *   rg = tangent-space normal xy, b = luminance, a = ambient occlusion.
 */
export function detailTexture(N, height, luma, ao, bump = 6.0) {
  const data = new Uint8Array(N * N * 4);
  for (let j = 0; j < N; j++) {
    const jm = ((j - 1 + N) % N) * N, jp = ((j + 1) % N) * N, jr = j * N;
    for (let i = 0; i < N; i++) {
      const im = (i - 1 + N) % N, ip = (i + 1) % N;
      const nx = (height[jr + im] - height[jr + ip]) * bump;
      const ny = (height[jm + i] - height[jp + i]) * bump;
      const k = (jr + i) * 4;
      data[k] = Math.max(0, Math.min(255, (nx * 0.5 + 0.5) * 255)) | 0;
      data[k + 1] = Math.max(0, Math.min(255, (ny * 0.5 + 0.5) * 255)) | 0;
      data[k + 2] = Math.max(0, Math.min(255, luma[jr + i] * 255)) | 0;
      data[k + 3] = Math.max(0, Math.min(255, (ao ? ao[jr + i] : 1) * 255)) | 0;
    }
  }
  const tex = new THREE.DataTexture(data, N, N, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;
  return tex;
}

/**
 * A tangent-space normal map, Sobel-derived from a tiling height field.
 *
 * `detailTexture` already packs a normal into rg, but it packs luma and AO
 * into ba and is consumed by the terrain shader's own unpack; this returns the
 * ordinary rgb encoding a normal-map sampler expects, including a real z, so
 * `surfaceMaterial({ normalMap })` can use it directly.
 *
 * Neighbours wrap, so the result tiles exactly as the height field does.
 * The texture is LINEAR data, never sRGB: an sRGB decode on a normal map bends
 * every vector toward the surface and the relief quietly flattens.
 */
export function normalTexture(N, height, { bump = 3.0, aniso = 4, mips = true } = {}) {
  const data = new Uint8Array(N * N * 4);
  for (let j = 0; j < N; j++) {
    const jm = ((j - 1 + N) % N) * N, jp = ((j + 1) % N) * N, jr = j * N;
    for (let i = 0; i < N; i++) {
      const im = (i - 1 + N) % N, ip = (i + 1) % N;
      const dx = (height[jr + ip] - height[jr + im]) * bump;
      const dy = (height[jp + i] - height[jm + i]) * bump;
      let nx = -dx, ny = -dy, nz = 1;
      const l = Math.sqrt(nx * nx + ny * ny + 1);
      nx /= l; ny /= l; nz /= l;
      const k = (jr + i) * 4;
      data[k] = ((nx * 0.5 + 0.5) * 255) | 0;
      data[k + 1] = ((ny * 0.5 + 0.5) * 255) | 0;
      data[k + 2] = ((nz * 0.5 + 0.5) * 255) | 0;
      data[k + 3] = 255;
    }
  }
  const tex = new THREE.DataTexture(data, N, N, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = mips ? THREE.LinearMipmapLinearFilter : THREE.LinearFilter;
  tex.generateMipmaps = mips;
  tex.anisotropy = aniso;
  tex.needsUpdate = true;
  return tex;
}

/** Make an sRGB colour DataTexture from a Uint8 RGBA buffer. */
export function colorTexture(data, w, h, { repeat = false, srgb = true, aniso = 4, mips = true } = {}) {
  const tex = new THREE.DataTexture(data, w, h, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.wrapS = tex.wrapT = repeat ? THREE.RepeatWrapping : THREE.ClampToEdgeWrapping;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = mips ? THREE.LinearMipmapLinearFilter : THREE.LinearFilter;
  tex.generateMipmaps = mips;
  tex.anisotropy = aniso;
  if (srgb) tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

/** Linear-data DataTexture (control maps, height fields). */
export function dataTexture(data, w, h, { repeat = false, linear = true, mips = false } = {}) {
  const tex = new THREE.DataTexture(data, w, h, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.wrapS = tex.wrapT = repeat ? THREE.RepeatWrapping : THREE.ClampToEdgeWrapping;
  tex.magFilter = linear ? THREE.LinearFilter : THREE.NearestFilter;
  tex.minFilter = mips ? THREE.LinearMipmapLinearFilter : (linear ? THREE.LinearFilter : THREE.NearestFilter);
  tex.generateMipmaps = mips;
  tex.needsUpdate = true;
  return tex;
}

/* ------------------------------------------------------------ canvas helper */
export function canvas(size, h = size) {
  const c = document.createElement('canvas');
  c.width = size; c.height = h;
  return { c, g: c.getContext('2d') };
}
export function canvasTexture(c, { repeat = true, srgb = true, aniso = 4 } = {}) {
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = repeat ? THREE.RepeatWrapping : THREE.ClampToEdgeWrapping;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.anisotropy = aniso;
  if (srgb) tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}
